/* eslint-disable  @typescript-eslint/no-explicit-any */

/**
 * `Opayo` integrates the Opayo (SagePay) client-side tokenisation flow.
 *
 * Flow summary:
 * - Loads the external SagePay script on mount.
 * - When `submitPayment` is triggered, validates form fields, obtains (or reuses)
 *   a merchant session key, then tokenises card details to a `cardIdentifier`.
 * - Submits the payment via `PaymentApi.submitPayment`.
 * - Handles 3DS authentication by showing an iframe modal and polling for the
 *   bank response.
 * - Supports voiding a transaction via `PaymentApi.voidPayment`.
 */

import React, { useState, useEffect } from 'react';
import { PaymentApi } from '../../../api/PaymentApi';
import { CardPayment, PaymentSuccessResponse } from '../CommonTypes';
//@ts-ignore
import { CardDateInput } from '../CardDateInput/CardDateInput';
import { Modal, ModalBody } from 'reactstrap';
import './Opayo.scss';
import AutoSubmitForm from '../AutoSubmitForm';
import { CommonValidation } from '../CommonValidation/commonValidation';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import TextInput from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import { SagePayAccount } from '../../../consts/Common';

declare const sagepayOwnForm: any;

export type MerchantSessionKey = {
  merchantSessionKey: string;
  expiry: string;
};

type TransactionResponse = {
  threeDUrl: string;
  cReq: string;
  vendorTransactionCode: string;
  success: boolean;
  bankAuthorisationCode: string;
};

export type BankResponse = {
  paymentStatus: string;
  bankAuthorisationCode: string;
};

type OpayoProps = {
  token: string;
  account: SagePayAccount;
  sagePayBaseUrl: string;
  submitPayment: boolean;
  payment: CardPayment;
  voidTransaction: boolean;
  countryCode: string;
  onLoaded: () => void;
  onError: () => void;
  onPaymentSuccess: (
    transactionId: string,
    bankAuthorisationCode: string
  ) => void;
  onPaymentFailure: () => void;
  onPaymentVoided: () => void;
  onInvalidPaymentForm: () => void;
  cardHeaderText: string;
  onThreeDAuth?: () => void;
};

type PaymentFormErrors = {
  expiryDate: string;
  cardNumber: string;
  cardHolderName: string;
  securityCode: string;
  cardIdentifier: string[];
  declinedError: string;
  failedPayment: string;
};

type CardIdentifierError = {
  code: number;
  message: string;
};

type CardIdentifierResponse = {
  success: boolean;
  cardIdentifier: string;
  httpErrorCode: number;
  errors: CardIdentifierError[];
};

const initialPaymentErrors = {
  expiryDate: '',
  cardNumber: '',
  cardHolderName: '',
  securityCode: '',
  cardIdentifier: [],
  declinedError: '',
  failedPayment: ''
};

const Opayo = ({
  token,
  account,
  sagePayBaseUrl,
  submitPayment,
  payment,
  voidTransaction,
  countryCode,
  onLoaded,
  onError,
  onPaymentSuccess,
  onPaymentFailure,
  onPaymentVoided,
  onInvalidPaymentForm,
  cardHeaderText,
  onThreeDAuth
}: OpayoProps): JSX.Element => {
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [sessionKey, setSessionKey] = useState<undefined | MerchantSessionKey>(
    undefined
  );
  const [cardIdentifier, setCardIdentifier] = useState('');
  const [sessionKeyLoading, setSessionKeyLoading] = useState(false);
  const [paymentFormErrors, setPaymentFormErrors] =
    useState<PaymentFormErrors>(initialPaymentErrors);
  const [show3DModal, setShow3DModal] = useState(false);
  const [transactionResponse, setTransactionResponse] = useState<
    TransactionResponse | undefined
  >(undefined);
  const [transactionId, setTransactionId] = useState('');
  const [bankResponseReady, setBankResponseReady] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const loadError = () => {
      onError();
    };

    const scriptLoaded = () => {
      onLoaded();
    };

    const LoadExternalScript = () => {
      const currentTime = new Date().getTime();
      const externalScript = document.createElement('script');
      externalScript.onerror = loadError;
      externalScript.id = 'sagePay';
      externalScript.async = true;
      externalScript.type = 'text/javascript';
      externalScript.setAttribute('crossorigin', 'anonymous');
      externalScript.onload = scriptLoaded;
      document.body.appendChild(externalScript);
      externalScript.src = `${sagePayBaseUrl}js/sagepay.js?v=${currentTime}`;
    };

    LoadExternalScript();
  }, []);

  useEffect(() => {
    if (submitPayment) {
      if (validatePaymentForm()) {
        if (sessionKeyLoading === false && !sessionKeyIsValid()) {
          setSessionKeyLoading(true);
        } else {
          //we have a valid merchant session key already
          identifyCard();
        }
      } else {
        onInvalidPaymentForm();
      }
    }
  }, [submitPayment]);

  useEffect(() => {
    if (sessionKeyLoading) {
      const controller = new AbortController();

      const callback = (data: MerchantSessionKey) => {
        console.log('Session key obtained');
        setSessionKey(data);
        setSessionKeyLoading(false);
      };

      const callbackFailure = () => {
        console.error('Session key failed to be obtained');
        setSessionKeyLoading(false);
      };

      new PaymentApi().getMerchantSessionKey(
        token,
        account,
        countryCode,
        callback,
        callbackFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    } else {
      if (submitPayment) {
        identifyCard();
      }
    }
  }, [sessionKeyLoading]);

  useEffect(() => {
    const controller = new AbortController();

    const callback = (data: PaymentSuccessResponse) => {
      const declinedStatusCode = '2000';
      setTransactionId(data.transactionId);

      if (data.success) {
        if (data.do3dAuth) {
          const updatedTransactionResponse = {
            threeDUrl: data.acsUrl,
            cReq: data.cReq,
            vendorTransactionCode: data.transactionId,
            success: false,
            bankAuthorisationCode: ''
          };

          setTransactionResponse(updatedTransactionResponse);
        } else {
          onPaymentSuccess(data.transactionId, data.bankAuthorisationCode);
        }
      } else {
        if (data.statusCode === declinedStatusCode) {
          const error: PaymentFormErrors = {
            ...initialPaymentErrors,
            declinedError: data.errorMessage
          };
          setPaymentFormErrors(error);
          setSessionKey(undefined);
          onInvalidPaymentForm();
        } else {
          paymentFailed();
        }
      }
    };

    const failureCallback = () => {
      paymentFailed();
    };

    if (cardIdentifier !== '') {
      new PaymentApi().submitPayment(
        cardIdentifier,
        sessionKey?.merchantSessionKey || '',
        payment,
        token,
        account,
        countryCode,
        callback,
        failureCallback,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [cardIdentifier]);

  useEffect(() => {
    if (transactionResponse) {
      if (transactionResponse.success) {
        onPaymentSuccess(
          transactionResponse.vendorTransactionCode,
          transactionResponse.bankAuthorisationCode
        );
      } else {
        setShow3DModal(true);
      }
    }
  }, [transactionResponse]);

  useEffect(() => {
    const controller = new AbortController();

    if (voidTransaction === true) {
      const callback = () => {
        onPaymentVoided();
      };

      const failureCallback = () => {
        onPaymentVoided();
      };

      new PaymentApi().voidPayment(
        transactionId,
        countryCode,
        token,
        account,
        callback,
        failureCallback,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [voidTransaction]);

  useEffect(() => {
    if (bankResponseReady) {
      const controller = new AbortController();
      const callback = (data: BankResponse) => {
        console.log(data);
        if (data.paymentStatus === 'Ok') {
          onPaymentSuccess(
            transactionResponse?.vendorTransactionCode || '',
            data.bankAuthorisationCode
          );
        } else {
          paymentFailed();
        }
      };

      const failureCallback = () => {
        console.log(
          `Bank response failed to be retrieved. vendor transaction id: ${transactionId}`
        );
        paymentFailed();
      };

      new PaymentApi().getBankResponse(
        transactionId,
        token,
        callback,
        failureCallback,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    }
  }, [bankResponseReady]);

  useEffect(() => {
    if (show3DModal) {
      onThreeDAuth && onThreeDAuth();
    }
  }, [show3DModal]);

  const sessionKeyIsValid = (): boolean => {
    if (sessionKey === undefined) {
      return false;
    }

    const now = new Date();
    const expiryDate = new Date(sessionKey.expiry);

    return now < expiryDate;
  };

  const extractErrorMessages = (result: CardIdentifierResponse): string[] => {
    if (result.success) {
      return [];
    }

    const invalidOrMissingValues = 422;
    const genericErrorMessage = [
      'An error has occured, please check your details and try again'
    ];

    if (
      result.httpErrorCode === invalidOrMissingValues &&
      result.errors.length > 0
    ) {
      const errorMessages = result.errors.map(
        (e: CardIdentifierError) => e.message
      );

      return errorMessages;
    }

    return genericErrorMessage;
  };

  const identifyCard = (): void => {
    const tokenComplete = (result: CardIdentifierResponse) => {
      if (result.success) {
        setCardIdentifier(result.cardIdentifier);
      } else {
        const cardIdentifierErrorMessages = extractErrorMessages(result);
        const error: PaymentFormErrors = {
          ...initialPaymentErrors,
          cardIdentifier: cardIdentifierErrorMessages
        };
        setPaymentFormErrors(error);
        onInvalidPaymentForm();
      }
    };

    const cardDetails = {
      cardholderName: cardHolderName,
      cardNumber: cardNumber,
      expiryDate: expiryMonth + expiryYear,
      securityCode: securityCode
    };

    sagepayOwnForm({
      merchantSessionKey: sessionKey?.merchantSessionKey || ''
    }).tokeniseCardDetails({
      cardDetails: cardDetails,
      // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
      onTokenised: (result: any) => tokenComplete(result)
    });
  };

  const getNumberValuesFromString = (stringInput: string) => {
    const numberPattern = /\d+/g;
    const numberWithoutLetters = stringInput?.match(numberPattern)?.join('');

    return numberWithoutLetters ?? '';
  };

  const onChangeCardNumber = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;

    const newValue =
      CommonValidation.isEmpty(value) || isNaN(parseInt(value))
        ? ''
        : getNumberValuesFromString(value);

    setCardNumber(newValue);
  };

  const onChangeExpiryDate = (
    e: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    const value = (e.target as HTMLInputElement).value;

    const newValue =
      CommonValidation.isEmpty(value) || isNaN(parseInt(value))
        ? ''
        : getNumberValuesFromString(value);

    switch (key) {
      case 'month':
        setExpiryMonth(newValue);
        break;
      case 'year':
        setExpiryYear(newValue);
        break;
      default:
    }
  };

  const onChangeSecurityCode = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;

    const newValue =
      CommonValidation.isEmpty(value) || isNaN(parseInt(value))
        ? ''
        : getNumberValuesFromString(value);

    setSecurityCode(newValue);
  };

  const isCardExpired = (
    expMonth: number,
    expYear: number,
    currentYear: number
  ): boolean => {
    const currentMonth = new Date().getMonth() + 1;
    const isMonthExpired = expMonth < currentMonth;
    const isCurrentYear = expYear === currentYear;

    return isCurrentYear && isMonthExpired;
  };

  const validatePaymentForm = (): boolean => {
    let isValid = true;

    const cardHolderNameCharLimit = 45;

    const error: PaymentFormErrors = { ...initialPaymentErrors };

    if (
      CommonValidation.isEmpty(expiryMonth) ||
      CommonValidation.isEmpty(expiryYear)
    ) {
      isValid = false;
      error.expiryDate = t('customer-single-payment.expiry-date-required');
    } else {
      const intMonth = parseInt(expiryMonth);
      const currentYear = new Date().getFullYear().toString().substr(-2);

      const isMonthInvalid = intMonth > 12 || intMonth < 1;
      const isYearInvalid = parseInt(expiryYear) < parseInt(currentYear);

      if (isMonthInvalid && isYearInvalid) {
        isValid = false;
        error.expiryDate = t(
          'customer-single-payment.invalid-expiry-date-error'
        );
      } else {
        if (isMonthInvalid) {
          isValid = false;
          error.expiryDate = t(
            'customer-single-payment.invalid-expiry-month-error'
          );
        } else if (isYearInvalid) {
          isValid = false;
          error.expiryDate = t(
            'customer-single-payment.invalid-expiry-year-error'
          );
        } else if (
          isCardExpired(intMonth, parseInt(expiryYear), parseInt(currentYear))
        ) {
          isValid = false;
          error.expiryDate = t('customer-single-payment.card-expired-error');
        }
      }
    }

    if (CommonValidation.isEmpty(cardNumber)) {
      isValid = false;
      error.cardNumber = t('customer-single-payment.card-number-required');
    }

    if (CommonValidation.isEmpty(cardHolderName)) {
      isValid = false;
      error.cardHolderName = t('customer-single-payment.name-on-card-required');
    }

    if (
      !CommonValidation.isValidLength(cardHolderName, cardHolderNameCharLimit)
    ) {
      isValid = false;
      error.cardHolderName = t(
        'customer-single-payment.cardholdername-exceeds-length',
        {
          cardHolderNameCharLimit: cardHolderNameCharLimit
        }
      );
    }

    if (CommonValidation.isEmpty(securityCode)) {
      isValid = false;
      error.securityCode = t('customer-single-payment.cvv-empty-error');
    } else {
      if (securityCode.length != 3) {
        isValid = false;
        error.securityCode = t('customer-single-payment.cvv-invalid-error');
      }
    }

    setPaymentFormErrors(error);

    return isValid;
  };

  const sessionData = transactionId;

  const onIFrameLoad = (e: React.SyntheticEvent) => {
    const target = e.currentTarget as HTMLIFrameElement;
    const contentDoc = target.contentDocument;

    if (contentDoc != null) {
      setBankResponseReady(true);
    } else {
      console.log('iframe load but not redirect');
    }
  };

  const paymentFailed = () => {
    resetPayment();
    onPaymentFailure();
  };

  const resetPayment = () => {
    setCardHolderName('');
    setCardNumber('');
    setExpiryMonth('');
    setExpiryYear('');
    setSecurityCode('');
    setSessionKey(undefined);
    setCardIdentifier('');
    setSessionKeyLoading(false);

    const error: PaymentFormErrors = {
      ...initialPaymentErrors,
      failedPayment: 'Your payment failed to be taken'
    };
    setPaymentFormErrors(error);

    setShow3DModal(false);
    setTransactionResponse(undefined);
    setTransactionId('');
    setBankResponseReady(false);
  };

  return (
    <div>
      {submitPayment !== true && (
        <>
          {cardHeaderText && (
            <h4 className='section-header'>{cardHeaderText}</h4>
          )}
          <label className='control-label' htmlFor='txtCardHolder'>
            {t('customer-single-payment.name-on-card')}
          </label>
          <div className='control'>
            <TextInput
              name='txtCardHolder'
              className='input-text'
              placeholder={t('customer-single-payment.name-on-card')}
              value={cardHolderName}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setCardHolderName((e.target as HTMLInputElement).value)
              }
              error={paymentFormErrors.cardHolderName}
            />
          </div>
          <label className='control-label' htmlFor='txtCardNumber'>
            {t('customer-single-payment.card-number')}
          </label>
          <div className='control'>
            <TextInput
              name='txtCardNumber'
              className='input-text'
              placeholder={t('customer-single-payment.card-number')}
              value={cardNumber}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeCardNumber(e)
              }
              error={paymentFormErrors.cardNumber}
            />
          </div>
          <label className='control-label' htmlFor='month'>
            {t('customer-single-payment.expiry-date')}
          </label>
          <div className='flex-row'>
            <div className='inline-control-all-screen'>
              <div className='control'>
                <CardDateInput
                  label=''
                  ariaLabel={t('customer-single-payment.expiry-date')}
                  month={expiryMonth || ''}
                  year={expiryYear || ''}
                  error={paymentFormErrors.expiryDate}
                  onChange={(
                    e: React.FormEvent<HTMLInputElement>,
                    key: string
                  ) => onChangeExpiryDate(e, key)}
                />
              </div>
            </div>
            <div className='inline-control-all-screen'>
              <label className='control-label mt-0' htmlFor='txtSecurityCode'>
                {t('customer-single-payment.cvv-label')}
              </label>
              <div className='control card-cvv'>
                <TextInput
                  name='txtSecurityCode'
                  className='input-text'
                  placeholder='E.g. 123'
                  value={securityCode}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeSecurityCode(e)
                  }
                  error={paymentFormErrors.securityCode}
                  maxLength={3}
                />
              </div>
            </div>
          </div>
          <div className='error-style'>
            {paymentFormErrors.cardIdentifier.length > 0 &&
              paymentFormErrors.cardIdentifier.map((errorMessage, i) => (
                <div key={`error-${i}`}>{errorMessage}</div>
              ))}
          </div>
          {paymentFormErrors.declinedError !== '' && (
            <div className='error-style'>
              <div>{paymentFormErrors.declinedError}</div>
            </div>
          )}
          {paymentFormErrors.failedPayment !== '' && (
            <div className='error-style'>
              <div>{paymentFormErrors.failedPayment}</div>
            </div>
          )}
        </>
      )}

      {submitPayment === true && (
        <LoadingContainer
          loading={submitPayment}
          message={t('customer-single-payment.register-policy')}
        />
      )}

      <Modal isOpen={show3DModal}>
        <ModalBody>
          <div className='iframe-style'>
            {show3DModal === true && (
              <iframe
                name='3Diframe'
                width='400px'
                height='600px'
                src={transactionResponse?.threeDUrl || ''}
                onLoad={onIFrameLoad}
                sandbox='allow-forms allow-popups allow-pointer-lock allow-same-origin allow-scripts allow-top-navigation'>
                <AutoSubmitForm
                  actionUrl={transactionResponse?.threeDUrl || ''}
                  params={[
                    { name: 'creq', value: transactionResponse?.cReq || '' },
                    { name: 'threeDSSessionData', value: sessionData }
                  ]}
                  target='3Diframe'
                />
              </iframe>
            )}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Opayo;
